import { type MouseEvent, useCallback } from "react";
import { useNavigate } from "react-router-dom";

import { cn } from "@lib/cn.ts";

interface Props {
  html?: string;
  className?: string;
  size?: "sm" | "md";
}

/**
 * Renders the HTML that `pnpm content` pre-rendered from the spec's Markdown
 * descriptions.
 *
 * The content is our own authored YAML compiled at build time -- never user
 * input -- so there is nothing here to sanitise against. Internal links are
 * intercepted and pushed through the router so cross-references stay a
 * client-side navigation instead of a full page reload.
 */
export function Prose({ html, className, size = "md" }: Props) {
  const navigate = useNavigate();

  const onClick = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      const anchor = (event.target as HTMLElement).closest("a");
      if (!anchor) return;

      const href = anchor.getAttribute("href");
      if (!href?.startsWith("/")) return;

      // Let modified clicks (new tab, download, etc.) behave natively.
      if (
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.button !== 0
      ) {
        return;
      }

      event.preventDefault();
      void navigate(href);
    },
    [navigate],
  );

  if (!html) return null;

  return (
    <div
      onClick={onClick}
      className={cn("gt-prose", size === "sm" && "gt-prose-sm", className)}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
